import {
  getRegisteredPersistStores,
  type PersistStoreApi,
} from '@app/core/stores/persistence-registry';
import { ReactNode, useEffect, useState } from 'react';

/**
 * Returns true when all given persisted stores have finished hydrating.
 * Uses persist.hasHydrated() for synchronous check and onFinishHydration for async updates.
 */
function allStoresHydrated(stores: PersistStoreApi[]): boolean {
  return stores.length === 0 || stores.every((store) => store.persist?.hasHydrated?.() ?? true);
}

export interface ZustandPersistGateProps {
  children: ReactNode;
  /** Shown until all registered persisted stores have hydrated. Defaults to a centered spinner. */
  fallback?: ReactNode;
}

/**
 * Renders children only after all Zustand persisted stores registered in the
 * persistence registry have finished rehydrating. Uses persist.hasHydrated()
 * and onFinishHydration so the app does not render with initial state before
 * persisted values (e.g. timezone, locale, drawer state) are applied.
 */
export function ZustandPersistGate({ children, fallback }: ZustandPersistGateProps) {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const stores = getRegisteredPersistStores();

    const checkAllHydrated = () => {
      if (allStoresHydrated(stores)) {
        setHydrated(true);
      }
    };

    if (allStoresHydrated(stores)) {
      setHydrated(true);
      return;
    }

    const unsubscribes = stores
      .filter((store) => store.persist && !store.persist.hasHydrated())
      .map((store) => store.persist!.onFinishHydration(checkAllHydrated));

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, []);

  if (!hydrated) {
    return fallback ?? null;
  }

  return <>{children}</>;
}
